import React, { forwardRef, useImperativeHandle, useState } from 'react';

import {
  Button,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { FieldArray, Form, Formik } from 'formik';
import { MdAdd, MdClose } from 'react-icons/md';
import { useMutation, useQueryClient } from 'react-query';

import { createManufacturer, updateManufacturer } from 'api/manufacturer';
import FormikInput from 'components/primitives/FormikInput';
import { Manufacturer } from 'types/manufacturer';

import {
  ManufacturerFormValues,
  toFormValues,
  toPayload,
  validationSchema,
} from './ManufacturerModal.utils';

export type ManufacturerModalRef = {
  /** Opens the form to edit [manufacturer], or blank to add one. */
  onOpen: (manufacturer?: Manufacturer) => void;
};

/** Query keys showing manufacturers, refreshed after a save. */
export const MANUFACTURER_QUERY_KEYS = [
  'fetch-manufacturers-table',
  'fetch-all-manufacturers',
];

const labelProps = { fontSize: '12px', color: '#afafaf' };

/**
 * Adds or edits a manufacturer on Inventory → Manufacturers (ClickUp
 * 86bceudwp): every field of the manufacturer, with emails as a list and the
 * address as separate fields. Only super admins can open it.
 */
export const ManufacturerModal = forwardRef<ManufacturerModalRef>(
  (_props, ref) => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [editing, setEditing] = useState<Manufacturer | null>(null);

    useImperativeHandle(ref, () => ({
      onOpen: (manufacturer) => {
        setEditing(manufacturer ?? null);
        onOpen();
      },
    }));

    const { mutate, isLoading } = useMutation(
      (values: ManufacturerFormValues) =>
        editing
          ? updateManufacturer({ _id: editing._id, ...toPayload(values) })
          : createManufacturer(toPayload(values)),
      {
        onSuccess: () => {
          toast({
            title: editing ? 'Manufacturer updated' : 'Manufacturer added',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          MANUFACTURER_QUERY_KEYS.forEach((key) =>
            queryClient.invalidateQueries(key),
          );
          onClose();
        },
        onError: (error: any) => {
          const message = error?.response?.data?.message;
          toast({
            title: 'Could not save the manufacturer',
            description: Array.isArray(message) ? message.join('\n') : message,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        },
      },
    );

    return (
      <Modal
        size="xl"
        isCentered
        isOpen={isOpen}
        onClose={onClose}
        scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent borderRadius="20px">
          <ModalCloseButton _focus={{ outline: 'none' }} />
          <ModalHeader color="support.500" textAlign="center">
            {editing ? 'Edit Manufacturer' : 'Add Manufacturer'}
          </ModalHeader>
          <ModalBody>
            <Formik
              initialValues={toFormValues(editing)}
              validationSchema={validationSchema}
              enableReinitialize
              onSubmit={(values) => mutate(values)}>
              {({ values, isValid }) => (
                <Form>
                  <Flex direction="column" gap="16px" p={4}>
                    <FormikInput
                      name="name"
                      label="Name"
                      labelProps={labelProps}
                      placeholder="Manufacturer name"
                    />

                    <Text {...labelProps}>Email</Text>
                    <FieldArray name="email">
                      {({ push, remove }) => (
                        <Flex direction="column" gap="8px">
                          {values.email.map((_, index) => (
                            <Flex key={index} gap="8px" align="flex-start">
                              <FormikInput
                                name={`email.${index}`}
                                aria-label={`Email ${index + 1}`}
                                placeholder="name@company.com"
                                containerProps={{ flex: 1 }}
                              />
                              {values.email.length > 1 && (
                                <IconButton
                                  aria-label={`Remove email ${index + 1}`}
                                  variant="ghost"
                                  icon={<MdClose />}
                                  onClick={() => remove(index)}
                                />
                              )}
                            </Flex>
                          ))}
                          <Button
                            alignSelf="flex-start"
                            size="sm"
                            variant="outline"
                            leftIcon={<MdAdd />}
                            onClick={() => push('')}>
                            Add email
                          </Button>
                        </Flex>
                      )}
                    </FieldArray>

                    <SimpleGrid columns={2} spacing="12px">
                      <FormikInput
                        name="addressLine1"
                        label="Address line 1"
                        labelProps={labelProps}
                        placeholder="1889 E Maule Ave"
                      />
                      <FormikInput
                        name="addressLine2"
                        label="Address line 2"
                        labelProps={labelProps}
                        placeholder="Suite J"
                      />
                      <FormikInput
                        name="city"
                        label="City"
                        labelProps={labelProps}
                      />
                      <FormikInput
                        name="state"
                        label="State"
                        labelProps={labelProps}
                      />
                      <FormikInput
                        name="zip"
                        label="Zip"
                        labelProps={labelProps}
                      />
                      <FormikInput
                        name="country"
                        label="Country"
                        labelProps={labelProps}
                      />
                    </SimpleGrid>

                    <FormikInput
                      name="quickbooksVendorId"
                      label="QuickBooks vendor ID"
                      labelProps={labelProps}
                    />

                    <Flex mt={6} justify="center" gap="10px">
                      <Button
                        variant="outline"
                        w="134px"
                        isDisabled={isLoading}
                        onClick={onClose}>
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        w="134px"
                        isLoading={isLoading}
                        isDisabled={!isValid}>
                        {editing ? 'Save' : 'Add'}
                      </Button>
                    </Flex>
                  </Flex>
                </Form>
              )}
            </Formik>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  },
);
